import { Button } from "@/components/ui/button";
import { cn } from "@repo/design-system/utils";
import { ShoppingCart, Zap } from "lucide-react";
import plur from "plur";
import type { ReactNode } from "react";

export interface VariantOrderFooterProps {
  selectedCount: number;
  totalFormatted: string;
  isAddingToCart: boolean;
  onAddToCart: () => void;
  onQuickBuy: () => void;
  /** Secondary action rendered alongside the buy buttons (e.g. Add to list). */
  addToListSlot?: ReactNode;
}

export function VariantOrderFooter({
  selectedCount,
  totalFormatted,
  isAddingToCart,
  onAddToCart,
  onQuickBuy,
  addToListSlot
}: VariantOrderFooterProps) {
  const hasSelection = selectedCount > 0;
  const disabled = !hasSelection || isAddingToCart;

  return (
    // Below sm the group dissolves (`max-sm:contents`) so the count and total
    // share one row and the three buttons wrap onto a full-width row of their
    // own; Quick Buy All and Add to cart were being clipped off a phone's edge.
    <div className="flex items-center justify-between gap-4 border-t bg-white px-4 py-3 max-sm:flex-wrap max-sm:gap-y-3 dark:border-gray-800 dark:bg-black">
      <p className="text-sm text-gray-500">
        {hasSelection
          ? `${selectedCount} ${plur("item", selectedCount)} selected`
          : "No items selected"}
      </p>

      <div className="flex items-center gap-4 max-sm:contents">
        {/* Order total sits next to the buttons it justifies, not across the footer from them */}
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wide text-gray-400">
            Order total
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {totalFormatted}
          </p>
        </div>

        <div className="flex gap-2 max-sm:w-full max-sm:[&>*]:flex-1">
          {addToListSlot}

          {/* Secondary action: outline, less visual weight than Add to Cart, with a
              small amber accent (icon + border tint) matching the single-item dialog's
              Quick Buy so it still reads as distinct from Add to list. */}
          <Button
            variant="outline"
            onClick={onQuickBuy}
            disabled={disabled}
            className="h-9 gap-1.5 border-amber-400 text-xs font-semibold"
          >
            <Zap className="h-4 w-4 text-amber-500" />
            Quick Buy All
          </Button>

          {/* Primary action: strongest fill, rightmost/terminal position */}
          <Button
            onClick={onAddToCart}
            disabled={disabled}
            className={cn(
              "h-9 gap-1.5 text-xs font-semibold",
              disabled
                ? "cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
            )}
          >
            <ShoppingCart className="h-4 w-4" />
            Add to cart
          </Button>
        </div>
      </div>
    </div>
  );
}
